import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  MENU_ITEM_EDIT,
  MENU_ITEM_EDIT_REPORT,
  MENU_ITEM_PREVIEW,
  MENU_ITEM_QR,
  MENU_ITEM_ZIP_DOWNLOAD,
  MENU_ITEM_BURN_ISO,
  MENU_ITEM_REPORT,
  MENU_ITEM_FILES,
  MENU_ITEM_STUDY_DELETE,
} from '@eva-pacs/core';
import { IconCatalog, DropdownMenuV2 as DropdownMenu, Button, ButtonVariant, ButtonSize } from '@evacenter/eden';

import { useStudyOptionMenuActions } from '~/hooks';

import { useSessionStore } from '../../../../store';
import { CAN_EDIT_REPORT, POLICIES } from '../../../../constants';

export interface StudyOptionMenuProps {
  /**
   * Descriptive text to be read to screenreaders
   */
  ariaLabel?: string;

  /**
   * Specify an optional className to be added to the component
   */
  className?: string;

  /**
   * Id of the study to interact with.
   */
  studyId: string;

  /**
   * Flag to indicate if report link should show or not
   */
  showReport?: boolean;

  /**
   * Flag to indicate if delete study link should show or not
   */
  showDeleteStudy?: boolean;
}

/**
 * This is a dropdown menu that shows the study options
 * @author Sergio Ruiz Davila<sergioruizdavila@gmail.com>
 * Created at 2021-07-26
 */
export const StudyOptionMenu: React.FC<StudyOptionMenuProps> = ({
  ariaLabel,
  className,
  studyId,
  showReport = false,
  showDeleteStudy = false,
}) => {
  const { t } = useTranslation();
  const user = useSessionStore((state) => state.user);

  const {
    performEditStudyAction,
    performPreviewAction,
    performReportViewAction,
    performBurnIsoAction,
    performCreateDownloadZipAction,
    performQRViewAction,
    performWatchFilesAction,
    performDeleteStudyAction,
  } = useStudyOptionMenuActions();

  const handleItemClick = (item: string) => {
    switch (item) {
      case MENU_ITEM_EDIT:
        performEditStudyAction(studyId);
        break;
      case MENU_ITEM_PREVIEW:
        performPreviewAction(studyId);
        break;
      case MENU_ITEM_REPORT:
        performReportViewAction(studyId);
        break;
      case MENU_ITEM_QR:
        performQRViewAction(studyId);
        break;
      case MENU_ITEM_ZIP_DOWNLOAD:
        performCreateDownloadZipAction(studyId);
        break;
      case MENU_ITEM_FILES:
        performWatchFilesAction(studyId);
        break;
      case MENU_ITEM_STUDY_DELETE:
        performDeleteStudyAction(studyId);
        break;
      case MENU_ITEM_BURN_ISO:
        performBurnIsoAction(studyId);
        break;
      case MENU_ITEM_EDIT_REPORT:
        window.open(`reportfullscreen/${studyId}`, '_blank');
        break;
      default:
        break;
    }
  };

  const canEditReport = POLICIES[CAN_EDIT_REPORT](user);
  const statusMenuClass = cn(className, 'z-30 lg:z-auto');

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button
          size={ButtonSize.xs}
          aria-label={ariaLabel}
          variant={ButtonVariant.ghost}
          endIcon={IconCatalog.moreVert}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={statusMenuClass}>
          <DropdownMenu.Label className="py-2 text-center">
            {t('study.dropdownMenu.optionsSubtitle')}
          </DropdownMenu.Label>
          <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_PREVIEW)}>
            {t('study.dropdownMenu.preview')}
          </DropdownMenu.Item>
          {showReport && (
            <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_REPORT)}>
              {t('study.dropdownMenu.report')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_FILES)}>
            {t('study.dropdownMenu.seeUploadFiles')}
          </DropdownMenu.Item>
          <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_QR)}>
            {t('general.modal.seeQR.title')}
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Label className="py-2 text-center">
            {t('study.dropdownMenu.actionsSubtitle')}
          </DropdownMenu.Label>
          <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_EDIT)}>
            {t('study.dropdownMenu.edit')}
          </DropdownMenu.Item>
          {canEditReport && (
            <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_EDIT_REPORT)}>
              {t('study.dropdownMenu.editReport')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            className="py-2 text-caption"
            onClick={() => handleItemClick(MENU_ITEM_ZIP_DOWNLOAD)}>{`${t(
            'general.download',
          )} DICOM`}</DropdownMenu.Item>
          <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_BURN_ISO)}>{`${t(
            'study.dropdownMenu.burn',
          )} CD`}</DropdownMenu.Item>
          {showDeleteStudy && (
            <DropdownMenu.Item className="py-2 text-caption" onClick={() => handleItemClick(MENU_ITEM_STUDY_DELETE)}>
              {t('study.dropdownMenu.deleteStudy')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Arrow />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
